import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { AssetType } from '../../types/asset';
import AssetTab from '../AssetsPanel/AssetTab';
import './ProjectSettingsModal.css';

interface ProjectSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type SettingsTab = AssetType;

const SETTINGS_TABS: { id: SettingsTab; label: string; icon: string }[] = [
  { id: 'materials', label: 'Materials', icon: 'fa-fill-drip' },
  { id: 'brushes', label: 'Brushes', icon: 'fa-paintbrush' },
  { id: 'props', label: 'Props', icon: 'fa-tree' }
];

/**
 * Modal for project-wide settings (global assets)
 */
function ProjectSettingsModal({ isOpen, onClose }: ProjectSettingsModalProps): JSX.Element | null {
  const [activeTab, setActiveTab] = useState<SettingsTab>('materials');
  const isDraggingRef = useRef<boolean>(false);

  /**
   * Handle escape key and track drag state
   */
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'Escape' && isOpen) {
        e.stopImmediatePropagation();
        onClose();
      }
    };

    const handleDragStart = (): void => {
      isDraggingRef.current = true;
    };

    const handleDragEnd = (): void => {
      // Delay reset to prevent mouseup from triggering close
      setTimeout(() => {
        isDraggingRef.current = false;
      }, 100);
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.addEventListener('dragstart', handleDragStart);
      document.addEventListener('dragend', handleDragEnd);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('dragstart', handleDragStart);
      document.removeEventListener('dragend', handleDragEnd);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  /**
   * Handle backdrop click - only close if clicking directly on backdrop and not dragging
   */
  const handleBackdropMouseDown = (e: React.MouseEvent<HTMLDivElement>): void => {
    if (e.target === e.currentTarget && !isDraggingRef.current) {
      onClose();
    }
  };

  const modalContent = (
    <div className="project-settings-backdrop" onMouseDown={handleBackdropMouseDown}>
      <div className="project-settings">
        <div className="project-settings-header">
          <span className="project-settings-title">
            <i className="fas fa-cog"></i>
            Project Settings
          </span>
          <button className="project-settings-close" onClick={onClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>

        <div className="project-settings-content">
          <div className="project-settings-sidebar">
            <div className="project-settings-section-title">Assets</div>
            {SETTINGS_TABS.map((tab) => (
              <button
                key={tab.id}
                className={`project-settings-nav-item ${activeTab === tab.id ? 'project-settings-nav-item--active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                <i className={`fas ${tab.icon}`}></i>
                <span>{tab.label}</span>
              </button>
            ))}
          </div>

          <div className="project-settings-main">
            <div className="project-settings-main-header">
              <h3>{SETTINGS_TABS.find(t => t.id === activeTab)?.label}</h3>
            </div>
            <div className="project-settings-main-content">
              <AssetTab assetType={activeTab} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}

export default ProjectSettingsModal;
